import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { columnasPresentes, ConmutadorVista, Kanban, useVista } from './Kanban.jsx';
import { api, ErrorApi } from '../lib/api.js';
import { aTexto } from '../lib/formato.js';
import { Cargando, Error as AvisoError, Vacio } from '../piezas/Estados.jsx';
import { useSesion } from './Sesion.jsx';

/**
 * Tabla declarativa.
 *
 * El motor del servidor probo que declarar una entidad vence a programarla.
 * Esto es la otra mitad: la lista, el orden, la busqueda, la paginacion, el
 * CSV y los tres estados —cargando, vacio, error— viven aqui una sola vez.
 * Una pantalla nueva declara sus columnas.
 *
 * Que importa de que este asi: el filtrado de montos, el estado vacio con
 * mensaje y el error con su codigo son cosas que se olvidan cuando se
 * escriben nueve veces. Aqui no se pueden olvidar porque no se escriben.
 */

export interface Columna<T> {
  /** Nombre de la columna en los datos. Tambien sirve para ordenar. */
  campo: string;
  /** Encabezado visible. */
  titulo: string;
  /** Alineacion a la derecha para cantidades. */
  num?: boolean;
  /** Si la columna lleva dinero: no se dibuja cuando el perfil no ve montos. */
  monto?: boolean;
  /** Si se puede ordenar por ella en el servidor. */
  ordenable?: boolean;
  /** Como se pinta la celda. Por omision, el valor tal cual. */
  celda?: (fila: T) => ReactNode;
  /** Oculta la columna en pantallas angostas. */
  secundaria?: boolean;
}

export interface Filtro {
  /** Parametro de consulta que se manda al servidor. */
  parametro: string;
  /** Opciones, en orden. La primera es la que arranca seleccionada. */
  valores: { valor: string; etiqueta: string }[];
}

interface Props<T> {
  /** Segmento de la entidad: `clientes`, `sitios`, `contratos-doc`. */
  ruta: string;
  columnas: Columna<T>[];
  /** Nombre de lo que se lista, para los mensajes. "los clientes". */
  que: string;
  /** Icono del estado vacio. */
  icono?: string;
  /** Parametros fijos de la consulta. */
  parametrosFijos?: Record<string, string>;
  /** Chips de filtro sobre un parametro. */
  filtro?: Filtro;
  /** Si el servidor ofrece busqueda por texto en esta entidad. */
  buscable?: boolean;
  /** Si se ofrece el boton de CSV. */
  csv?: boolean;
  /** Orden inicial. */
  orden?: string;
  descendente?: boolean;
  /** Acciones del encabezado: el boton de alta, por ejemplo. */
  acciones?: ReactNode;
  /** Que pasa al presionar una fila. Sin esto las filas no son presionables. */
  alElegir?: (fila: T) => void;
  /** Senal para recargar: cambiar este numero vuelve a pedir la lista. */
  recarga?: number;
  /** Columna que identifica la fila de forma estable. */
  llave: (fila: T) => string;
  /**
   * Si la entidad tiene maquina de estados, su kanban.
   *
   * Va aqui y no en cada pantalla porque la Tabla YA tiene las filas: pedirlas
   * otra vez para dibujarlas de otra forma seria pedir dos veces lo mismo, y
   * las dos vistas podrian acabar ensenando momentos distintos de la
   * operacion sin que nadie lo note.
   */
  kanban?: {
    estadoDe: (fila: T) => string;
    flujos: readonly { readonly estados: readonly string[] }[];
    etiquetas: Record<string, string>;
    clases?: Record<string, string>;
    tarjeta: (fila: T) => ReactNode;
    /** Para recordar la preferencia por pantalla. */
    pantalla: string;
  };
}

const POR_PAGINA = 50;

export function Tabla<T extends Record<string, unknown>>({
  ruta,
  columnas,
  que,
  icono = '📭',
  parametrosFijos,
  filtro,
  buscable = false,
  csv = false,
  orden: ordenInicial,
  descendente = false,
  acciones,
  kanban,
  alElegir,
  recarga = 0,
  llave,
}: Props<T>): JSX.Element {
  const [vista, ponerVista] = useVista(kanban?.pantalla ?? ruta);
  const [filas, setFilas] = useState<T[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [pagina, setPagina] = useState(0);
  const [orden, setOrden] = useState(ordenInicial ?? '');
  const [desc, setDesc] = useState(descendente);
  const [busqueda, setBusqueda] = useState('');
  const [texto, setTexto] = useState('');
  const [valorFiltro, setValorFiltro] = useState(filtro?.valores[0]?.valor ?? '');
  const { veMontos } = useSesion();

  /* Las columnas de dinero no se dibujan cuando el perfil no las recibe. Una
     columna de puros guiones solo informa que hay algo que no se puede ver, y
     el servidor ni siquiera manda la llave. */
  const visibles = useMemo(
    () => columnas.filter((c) => !c.monto || veMontos),
    [columnas, veMontos],
  );

  /* La busqueda espera a que la persona termine de escribir. Sin esto, cada
     tecla es una consulta con `ilike` sobre la tabla. */
  const temporizador = useRef<number | undefined>(undefined);
  useEffect(() => {
    window.clearTimeout(temporizador.current);
    temporizador.current = window.setTimeout(() => {
      setBusqueda(texto.trim());
      setPagina(0);
    }, 350);
    return () => window.clearTimeout(temporizador.current);
  }, [texto]);

  const consulta = useMemo(() => {
    const p = new URLSearchParams(parametrosFijos ?? {});
    p.set('limite', String(POR_PAGINA));
    if (pagina > 0) p.set('desde', String(pagina * POR_PAGINA));
    if (orden) p.set('orden', orden);
    if (desc) p.set('desc', '1');
    if (busqueda) p.set('q', busqueda);
    if (filtro && valorFiltro) p.set(filtro.parametro, valorFiltro);
    return p.toString();
  }, [parametrosFijos, pagina, orden, desc, busqueda, filtro, valorFiltro]);

  useEffect(() => {
    let vigente = true;
    setFilas(null);
    setError(null);
    api
      .obtener<T[]>(`/v1/${ruta}?${consulta}`)
      .then(({ datos, meta }) => {
        if (!vigente) return;
        setFilas(datos);
        setTotal(meta.total ?? datos.length);
      })
      .catch((e: unknown) => {
        if (vigente) setError(e);
      });
    /* La peticion anterior se ignora si llega tarde. Sin esto, teclear rapido
       deja en pantalla el resultado de una busqueda vieja. */
    return () => {
      vigente = false;
    };
  }, [ruta, consulta, recarga]);

  const ordenarPor = (campo: string): void => {
    if (orden === campo) setDesc(!desc);
    else {
      setOrden(campo);
      setDesc(false);
    }
    setPagina(0);
  };

  const paginas = Math.ceil(total / POR_PAGINA);

  return (
    <>
      {(buscable || filtro || csv || acciones) && (
        <div className="fila wrap" style={{ marginBottom: 12 }}>
          {buscable && (
            <div className="busca">
              <label className="visualmente-oculto" htmlFor={`busca-${ruta}`}>
                Buscar en {que}
              </label>
              <span aria-hidden="true">🔍</span>
              <input
                id={`busca-${ruta}`}
                type="search"
                value={texto}
                onChange={(ev) => setTexto(ev.target.value)}
                placeholder="Buscar…"
                data-prueba="busca"
              />
            </div>
          )}

          {filtro && (
            <div className="chips">
              {filtro.valores.map((v) => (
                <button
                  key={v.valor}
                  type="button"
                  className={valorFiltro === v.valor ? 'chip on' : 'chip'}
                  onClick={() => {
                    setValorFiltro(v.valor);
                    setPagina(0);
                  }}
                  data-prueba={`filtro-${v.valor || 'todos'}`}
                >
                  {v.etiqueta}
                </button>
              ))}
            </div>
          )}

          <span className="crece" />

          {total > 0 && (
            <span className="muted" style={{ fontSize: 12 }} data-prueba="total">
              {total} {total === 1 ? 'registro' : 'registros'}
            </span>
          )}

          {csv && (
            /* Descarga directa por el navegador: la API manda el CSV ya
               filtrado por perfil, con su BOM y su cabecera. Armarlo aqui
               seria una segunda implementacion del mismo documento. */
            <a className="btn sm" href={`/api/v1/${ruta}/csv`} download data-prueba="csv">
              ⬇ CSV
            </a>
          )}

          {kanban && <ConmutadorVista vista={vista} poner={ponerVista} />}
          {acciones}
        </div>
      )}

      {error !== null && <AvisoError error={error} />}

      <div className="card p0">
        {filas === null && error === null ? (
          <Cargando que={que} />
        ) : filas === null ? (
          <></>
        ) : filas.length === 0 ? (
          <Vacio
            mensaje={busqueda ? `Nada coincide con "${busqueda}"` : `Todavia no hay ${que}`}
            {...(busqueda ? { ayuda: 'Prueba con menos letras.' } : {})}
            icono={icono}
          />
        ) : kanban && vista === 'kanban' ? (
          <Kanban
            items={filas}
            estadoDe={kanban.estadoDe}
            claveDe={llave}
            columnas={columnasPresentes(filas.map(kanban.estadoDe), kanban.flujos)}
            etiquetas={kanban.etiquetas}
            {...(kanban.clases ? { clases: kanban.clases } : {})}
            tarjeta={kanban.tarjeta}
            {...(alElegir ? { alAbrir: alElegir } : {})}
          />
        ) : (
          <table data-prueba={`tabla-${ruta}`}>
            <thead>
              <tr>
                {visibles.map((c) => (
                  <th
                    key={c.campo}
                    className={[c.num ? 'num' : '', c.secundaria ? 'sec' : '']
                      .filter(Boolean)
                      .join(' ')}
                    aria-sort={orden === c.campo ? (desc ? 'descending' : 'ascending') : undefined}
                  >
                    {c.ordenable ? (
                      <button type="button" className="orden" onClick={() => ordenarPor(c.campo)}>
                        {c.titulo}
                        <span aria-hidden="true">
                          {orden === c.campo ? (desc ? ' ▾' : ' ▴') : ' ⇅'}
                        </span>
                      </button>
                    ) : (
                      c.titulo
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr
                  key={llave(f)}
                  {...(alElegir
                    ? {
                        className: 'elegible',
                        tabIndex: 0,
                        role: 'button',
                        onClick: () => alElegir(f),
                        /* Operable con teclado, no solo con raton: una fila
                           que solo responde al click deja fuera a quien
                           navega con tabulador. */
                        onKeyDown: (ev: React.KeyboardEvent) => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            alElegir(f);
                          }
                        },
                      }
                    : {})}
                >
                  {visibles.map((c) => (
                    <td
                      key={c.campo}
                      className={[c.num ? 'num' : '', c.secundaria ? 'sec' : '']
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {c.celda ? c.celda(f) : textoPlano(f[c.campo])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {paginas > 1 && (
        <div className="fila" style={{ marginTop: 12, justifyContent: 'center' }}>
          <button
            type="button"
            className="btn sm"
            onClick={() => setPagina(pagina - 1)}
            disabled={pagina === 0}
          >
            ← Anterior
          </button>
          <span className="muted" style={{ fontSize: 12 }} data-prueba="pagina">
            Pagina {pagina + 1} de {paginas}
          </span>
          <button
            type="button"
            className="btn sm"
            onClick={() => setPagina(pagina + 1)}
            disabled={pagina + 1 >= paginas}
          >
            Siguiente →
          </button>
        </div>
      )}
    </>
  );
}

/** Un valor desconocido, legible. Nunca `[object Object]` en una celda. */
function textoPlano(v: unknown): ReactNode {
  if (v === null || v === undefined || v === '') return <span className="muted">—</span>;
  return aTexto(v);
}

/** Un error de API, legible, para las pantallas que no usan Tabla. */
export function mensajeDe(error: unknown): string {
  if (error instanceof ErrorApi) return `${error.message} (${error.codigo})`;
  return 'No se pudo completar la operacion';
}
